

import React, { useRef, useEffect, useState, useMemo } from 'react';

import {
    normaliseRotation,
    handleRotationChange,
    getCurrentRotation
} from '../helpers/rotationUtils.js';
import { replaceUrlState } from '../helpers/urlUpdaterHelper.js';

import '../styles/phcolours.css';

import MirrorButton from '../../images/MirrorButton@3x.png';
import MirrorButtonHover from '../../images/MirrorButtonWhite@3x.png';

import useUIPresentationMode from '../helpers/useUIPresentationMode.js';


/*-----------------------------------------------------------------------------*/


const UIBottomButtons = ({
    isShiftDraggingRef,
    overlayIsMirrored,
    setOverlayIsMirrored
}) => {

    const sliderRef = useRef();
    const [isHoveringMirror, setIsHoveringMirror] = useState(false);
    const [displayRotation, setDisplayRotation] = useState(0);

    const { presentationModeSize } = useUIPresentationMode();
    const isVeryNarrowScreen = presentationModeSize >= 3;


    // Throttle rotation updates coming out of the rotation slider if we are on mobile - causes crashes.
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

    const throttle = (func, delay) => {
        let timeout = null;
        let lastArgs = null;

        return (...args) => {
            lastArgs = args;

            if (!timeout) {
                func(...lastArgs);
                timeout = setTimeout(() => {
                    timeout = null;
                }, delay);
            }
        };
    };

    // Create a throttled version of your handler
    const throttledRotationChange = useMemo(() => {
        const delay = isMobile ? 250 : 50; // 250ms on touch devices, 100ms on desktop
        return throttle(handleRotationChange, delay);
    }, [isMobile])


    useEffect(() => {

        let frameId;

        const update = () => {
            setDisplayRotation(normaliseRotation(getCurrentRotation()));
            frameId = requestAnimationFrame(update);
        };

        frameId = requestAnimationFrame(update);

        return () => cancelAnimationFrame(frameId);
    }, []);


    // Slider knob needs special treatment to set its colour apparently...
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
          input[type="range"] {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 6px;
            background: var(--phblue);
            border-radius: 4px;
            outline: none;
          }
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background-color: var(--phblue);
            border: none;
            cursor: pointer;
          }

          input[type="range"]::-moz-range-thumb {
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background-color: var(--phblue);
            border: none;
            cursor: pointer;
          }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);


    return (

        <div className="ui-bottom-buttons" style={{
            
                position: 'absolute',
                bottom: '25px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                padding: '10px 20px',
                borderRadius: '999px',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',  // Vertical alignment.
                flexWrap: 'wrap',
                gap: isVeryNarrowScreen ? '8px' : '15px',
                width: '90%',
                //minWidth: presentationModeSize === 1 ? '500px' :
                //    presentationModeSize === 2 ? '350px' :
                //        presentationModeSize === 3 ? '300px' :
                //            '250px',
                maxWidth: '700px',
                justifyContent: 'space-between',
            }}
        >

            <input
                ref={sliderRef}
                type="range"
                min={-180}
                max={180}
                step={0.5}
                value={displayRotation}
                onChange={e => {
                    if (!isShiftDraggingRef.current) {
                        throttledRotationChange(parseFloat(e.target.value), { updateURL: false });
                    }
                }}
                onMouseUp={() => handleRotationChange(getCurrentRotation(), { updateURL: true })}
                onTouchEnd={() => handleRotationChange(getCurrentRotation(), { updateURL: true })}
                style={{
                    flex: 1,
                    //flexGrow: 1,
                    minWidth: '100px', // Prevent slider from becoming ridiculously small
                    maxWidth: '100%',  // Allow it to shrink nicely inside parent
                    height: '6px',
                    borderRadius: '4px',
                    background: 'var(--phblue)',
                    appearance: 'none',
                    cursor: 'pointer',
                }}
            />

            <div
                onClick={() => handleRotationChange(0)}
                style={{
                    width: '90px',
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'color 0.2s',
                    color: displayRotation === 0 ? 'var(--phblueaccent)' : 'var(--phblue)',
                }}
                title="Click to reset to 0°"
            >
                {displayRotation.toFixed(1)}°
            </div>

            <button
                onClick={() => {
                    const newMirroredState = !overlayIsMirrored;
                    setOverlayIsMirrored(newMirroredState);

                    const url = new URL(window.location);
                    if (newMirroredState) {
                        url.searchParams.set('mr', '1');
                    } else {
                        url.searchParams.delete('mr');
                    }
                    replaceUrlState(url);
                }}
                onMouseEnter={() => setIsHoveringMirror(true)}
                onMouseLeave={() => setIsHoveringMirror(false)}
                style={{
                    padding: '6px',
                    border: 'none',
                    borderRadius: '999px',
                    backgroundColor: isHoveringMirror ? '#8794B4' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px'
                }}
                title="Mirror"
            >
                <img
                    src={isHoveringMirror ? MirrorButtonHover : MirrorButton}
                    alt="Mirror"
                    style={{
                        width: '20px',
                        height: '20px',
                        objectFit: 'contain',
                    }}
                />
            </button>

        </div>
    );
};

export default UIBottomButtons;

